import React, { useState, useEffect, useCallback } from 'react';
import { appLoginsAPI } from '../services/api';
import { FiRefreshCw, FiLock, FiUnlock, FiSearch } from 'react-icons/fi';

const TYPE_LABELS = { student: 'Student', alumni: 'Alumni', staff: 'Staff' };
const TYPE_COLORS = {
  student: { bg: '#e0e7ff', color: '#3730a3' },
  alumni:  { bg: '#fef3c7', color: '#92400e' },
  staff:   { bg: '#dbeafe', color: '#1e40af' },
};

function timeSince(iso) {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function AppLogins() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [blockModal, setBlockModal] = useState(null); // { user, action }
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await appLoginsAPI.list();
      const list = res.data?.results || [];
      setUsers(list);
      setFiltered(list);
    } catch (e) {
      setError('Failed to load app logins.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let list = users;
    if (typeFilter !== 'all') list = list.filter(u => u.login_type === typeFilter);
    if (statusFilter === 'blocked') list = list.filter(u => u.is_blocked);
    if (statusFilter === 'active') list = list.filter(u => !u.is_blocked);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.student_id?.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [users, search, typeFilter, statusFilter]);

  const openBlock = (user) => { setBlockModal({ user, action: 'block' }); setBlockReason(''); };
  const openUnblock = (user) => { setBlockModal({ user, action: 'unblock' }); setBlockReason(''); };

  const confirmBlock = async () => {
    if (!blockModal) return;
    if (blockModal.action === 'block' && !blockReason.trim()) {
      alert('Please enter a reason for blocking.');
      return;
    }
    setBlocking(true);
    try {
      if (blockModal.action === 'block') {
        await appLoginsAPI.block(blockModal.user.id, blockReason.trim());
      } else {
        await appLoginsAPI.unblock(blockModal.user.id);
      }
      setBlockModal(null);
      setBlockReason('');
      load();
    } catch (e) {
      alert('Action failed. Please try again.');
    } finally {
      setBlocking(false);
    }
  };

  const counts = {
    total: users.length,
    students: users.filter(u => u.login_type === 'student').length,
    alumni: users.filter(u => u.login_type === 'alumni').length,
    staff: users.filter(u => u.login_type === 'staff').length,
    blocked: users.filter(u => u.is_blocked).length,
  };

  return (
    <>
    <div style={{ padding: '28px 32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Application Logins</h1>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
              All users who have logged into the VEC student app
            </p>
          </div>
          <button
            onClick={load}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
              borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14 }}
          >
            <FiRefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Logins', value: counts.total, color: '#011ff3' },
            { label: 'Students', value: counts.students, color: '#3730a3' },
            { label: 'Alumni', value: counts.alumni, color: '#92400e' },
            { label: 'Staff', value: counts.staff, color: '#1e40af' },
            { label: 'Blocked', value: counts.blocked, color: '#dc2626' },
          ].map(s => (
            <div key={s.label} style={{
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
              padding: '14px 20px', minWidth: 110, flex: '1 1 100px',
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 200px' }}>
            <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, or ID..."
              style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
                border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
            />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14 }}>
            <option value="all">All Types</option>
            <option value="student">Students</option>
            <option value="alumni">Alumni</option>
            <option value="staff">Staff</option>
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14 }}>
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#ef4444' }}>{error}</div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['ID', 'Name', 'Email', 'Type', 'Role', 'Last Login', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                      fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No users found</td></tr>
                ) : filtered.map(u => {
                  const tc = TYPE_COLORS[u.login_type] || TYPE_COLORS.student;
                  return (
                    <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6',
                      background: u.is_blocked ? '#fff7f7' : 'transparent' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{
                          fontFamily: 'monospace', fontSize: 11, fontWeight: 600,
                          background: u.student_id?.startsWith('STAFF-') ? '#dbeafe' :
                            u.student_id?.startsWith('ALUM-') ? '#fef3c7' : '#e0e7ff',
                          color: u.student_id?.startsWith('STAFF-') ? '#1e40af' :
                            u.student_id?.startsWith('ALUM-') ? '#92400e' : '#3730a3',
                          padding: '2px 8px', borderRadius: 10,
                        }}>{u.student_id}</span>
                      </td>
                      <td style={{ padding: '10px 14px', fontWeight: 500 }}>{u.name || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#6b7280', fontSize: 13 }}>{u.email}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ ...tc, padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>
                          {TYPE_LABELS[u.login_type] || u.login_type}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#6b7280', fontSize: 13 }}>
                        {u.role_label || '—'}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#6b7280', fontSize: 13 }}>
                        {timeSince(u.last_login)}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {u.is_blocked ? (
                          <span style={{ background: '#fee2e2', color: '#991b1b',
                            padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>
                            Blocked
                          </span>
                        ) : (
                          <span style={{ background: '#dcfce7', color: '#166534',
                            padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>
                            Active
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {u.is_blocked ? (
                          <button onClick={() => openUnblock(u)} title="Unblock user"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '5px 12px', borderRadius: 6, border: 'none',
                              background: '#dcfce7', color: '#166534', cursor: 'pointer', fontSize: 13 }}>
                            <FiUnlock size={13} /> Unblock
                          </button>
                        ) : (
                          <button onClick={() => openBlock(u)} title="Block user"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '5px 12px', borderRadius: 6, border: 'none',
                              background: '#fee2e2', color: '#991b1b', cursor: 'pointer', fontSize: 13 }}>
                            <FiLock size={13} /> Block
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Block / Unblock confirmation modal */}
      {blockModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setBlockModal(null)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420, maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17 }}>
              {blockModal.action === 'block' ? '🔒 Block User' : '🔓 Unblock User'}
            </h3>
            <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: 14 }}>
              {blockModal.action === 'block'
                ? `Block ${blockModal.user.name} from accessing the app?`
                : `Restore app access for ${blockModal.user.name}?`}
            </p>
            {blockModal.action === 'block' && (
              <textarea
                value={blockReason}
                onChange={e => setBlockReason(e.target.value)}
                placeholder="Reason for blocking (required)..."
                rows={3}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
                  fontSize: 14, resize: 'vertical', boxSizing: 'border-box', marginBottom: 16 }}
              />
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setBlockModal(null)}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb',
                  background: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Cancel
              </button>
              <button onClick={confirmBlock} disabled={blocking}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14,
                  fontWeight: 600,
                  background: blockModal.action === 'block' ? '#dc2626' : '#16a34a',
                  color: '#fff', opacity: blocking ? 0.7 : 1 }}>
                {blocking ? 'Please wait…' : blockModal.action === 'block' ? 'Block User' : 'Unblock User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
